import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { BulkSelectionProvider } from './contexts/BulkSelectionContext';
import { UploadProvider } from './contexts/UploadContext';
import { ProcessingProvider } from './contexts/ProcessingContext';
import { UploadProgressToast } from './components/upload/UploadProgressToast';
import { ProcessingProgressToast } from './components/assets/ProcessingProgressToast';
import { AuthPage } from './pages/AuthPage';
import { DashboardPage } from './pages/DashboardPage';
import { Stores as Connectors } from './pages/Stores';
import { Products } from './pages/Products';
import { ProductDetailPage } from './pages/ProductDetail';
import { BulkReviewPage } from './pages/BulkReviewPage';
import JobsPage from './pages/JobsPage';
import ApprovalPage from './pages/ApprovalPage';
import JobMonitorPage from './pages/JobMonitorPage';
import AsyncJobMonitorPage from './pages/AsyncJobMonitorPage';
import CreateJobsPage from './pages/CreateJobsPage';
import JobDetailsPage from './pages/JobDetailsPage';
import { BillingPage } from './pages/BillingPage';
import { TeamManagement } from './pages/TeamManagement';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { AuthCallback } from './pages/AuthCallback';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { UploadsPage } from './pages/UploadsPage';
import { UploadBatchDetailPage } from './pages/UploadBatchDetailPage';
import { AssetsPage } from './pages/AssetsPage';
import { BatchHistoryPage } from './pages/BatchHistoryPage';
import { BatchHistoryDetailPage } from './pages/BatchHistoryDetailPage';
import { ReviewDashboard } from './pages/ReviewDashboard';
import { UploadProductsPage } from './pages/UploadProductsPage';
import { UploadProductDetailPage } from './pages/UploadProductDetailPage';
import { AmazonExportPage } from './pages/AmazonExportPage';
import { FeedWatchPage } from './pages/FeedWatchPage';
import { SocialPostingPage } from './pages/SocialPostingPage';
import { SettingsPage } from './pages/SettingsPage';
import { Layout } from './components/layout/Layout';
import { Toaster } from './components/ui/toaster';
import { clearAllToasts } from './hooks/use-toast';
import { SubscriptionComponents } from './components/subscription/SubscriptionComponents';

// Component to handle navigation cleanup
const NavigationCleanup: React.FC = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Clear all toasts when navigating to a new route
    clearAllToasts();
  }, [location.pathname]);
  
  return null;
};

/**
 * Optimized AppContent with improved context hierarchy
 * SubscriptionProvider now wraps everything to prevent race conditions
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <>
      <NavigationCleanup />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<AuthPage />} />
        <Route path="/register" element={<AuthPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        
        {/* Protected routes */}
        <Route path="/*" element={
          <ProtectedRoute>
            <OrganizationProvider>
              <SubscriptionProvider>
                <BulkSelectionProvider>
                  <UploadProvider>
                    <ProcessingProvider>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<DashboardPage />} />
                          <Route path="/dashboard" element={<DashboardPage />} />
                          <Route path="/connectors" element={<Connectors />} />
                          <Route path="/stores" element={<Navigate to="/connectors" replace />} />
                          <Route path="/uploads" element={<UploadsPage />} />
                          <Route path="/uploads/:batchId" element={<UploadBatchDetailPage />} />
                          <Route path="/assets" element={<AssetsPage />} />
                          <Route path="/review" element={<ReviewDashboard />} />
                          <Route path="/batch-history" element={<BatchHistoryPage />} />
                          <Route path="/batch-history/:batchId" element={<BatchHistoryDetailPage />} />
                          <Route path="/upload-products" element={<UploadProductsPage />} />
                          <Route path="/upload-products/:id" element={<UploadProductDetailPage />} />
                          <Route path="/amazon-export" element={<AmazonExportPage />} />
                          <Route path="/feedwatch" element={<FeedWatchPage />} />
                          <Route path="/social-posting" element={<SocialPostingPage />} />
                          <Route path="/products" element={<Products />} />
                          <Route path="/products/bulk-review" element={<BulkReviewPage />} />
                          <Route path="/products/:id" element={<ProductDetailPage />} />
                          <Route path="/jobs" element={<JobsPage />} />
                          <Route path="/jobs/create" element={<CreateJobsPage />} />
                          <Route path="/jobs/monitor" element={<JobMonitorPage />} />
                          <Route path="/jobs/async-monitor" element={<AsyncJobMonitorPage />} />
                          <Route path="/jobs/details" element={<JobDetailsPage />} />
                          <Route path="/jobs/approval" element={<ApprovalPage />} />
                          <Route path="/billing" element={<BillingPage />} />
                          <Route path="/team" element={<TeamManagement />} />
                          <Route path="/settings" element={<SettingsPage />} />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </Layout>
                      <UploadProgressToast />
                      <ProcessingProgressToast />
                      <SubscriptionComponents />
                    </ProcessingProvider>
                  </UploadProvider>
                </BulkSelectionProvider>
              </SubscriptionProvider>
            </OrganizationProvider>
          </ProtectedRoute>
        } />
      </Routes>
      <Toaster />
    </>
  );
};

/**
 * Optimized App component with better context organization
 * 
 * Benefits of this structure:
 * 1. AuthProvider only handles authentication (single responsibility)
 * 2. SubscriptionProvider manages all subscription-related state
 * 3. No more race conditions between contexts
 * 4. Clearer data flow and dependencies
 */
function App() {
  // Initialize theme from localStorage or default to dark
  useEffect(() => {
    const storedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    // Default to dark theme if no preference stored
    const isDark = storedTheme ? storedTheme === 'dark' : true;
    
    document.documentElement.classList.toggle('dark', isDark);
    document.body.classList.add('bg-background', 'text-foreground');
    
    // Store the theme preference if not already stored
    if (!storedTheme) {
      localStorage.setItem('theme', 'dark');
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;